import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { GripVertical, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * DrawGrid — draggable, editable schedule grid (courts × time-slots).
 * Rows are 30-min time slots inferred from the earliest / latest scheduled_at.
 * Columns are courts (distinct court values in the current match set).
 * Ties can be dragged from one cell to another to reassign time + court in
 * a single gesture. Uses PATCH /api/matches/{id}/schedule for each of the
 * 3 seed matches inside a tie.
 */
const SLOT_MINUTES = 30;

function toDate(iso) {
  try { return new Date(iso); } catch { return null; }
}
function fmtDate(d) { return d.toISOString().slice(0, 10); }
function fmtTime(d) {
  return d.toISOString().slice(11, 16);
}
function fmtSlotLabel(d) {
  const h = d.getUTCHours().toString().padStart(2, "0");
  const m = d.getUTCMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}
function slotStart(d) {
  const clone = new Date(d);
  const mins = clone.getUTCMinutes();
  clone.setUTCMinutes(mins - (mins % SLOT_MINUTES), 0, 0);
  return clone;
}

/** Group all "group"-stage matches (with tie_id) into unified tie objects. */
function buildTies(matches, teamsById) {
  const map = new Map();
  matches.forEach((m) => {
    if (m.stage !== "group" || !m.tie_id) return;
    if (!map.has(m.tie_id)) {
      const ta = teamsById[m.team_a_id];
      const tb = teamsById[m.team_b_id];
      map.set(m.tie_id, {
        tie_id: m.tie_id,
        team_a: m.team_a || ta, team_b: m.team_b || tb,
        team_a_id: m.team_a_id, team_b_id: m.team_b_id,
        scheduled_at: m.scheduled_at,
        court: m.court,
        group: (ta && ta.group) || (tb && tb.group) || (m.team_a && m.team_a.group) || null,
        seeds: [],
      });
    }
    const tie = map.get(m.tie_id);
    tie.seeds.push(m);
    if (!tie.scheduled_at && m.scheduled_at) tie.scheduled_at = m.scheduled_at;
  });
  return [...map.values()];
}

export default function DrawGrid({ matches, courts = [], teams = [], onReload }) {
  const [dragTie, setDragTie] = useState(null);
  const [busyCells, setBusyCells] = useState({});

  const teamsById = useMemo(() => {
    const map = {};
    teams.forEach((t) => { map[t.id] = t; });
    return map;
  }, [teams]);

  const ties = useMemo(() => buildTies(matches, teamsById), [matches, teamsById]);

  // Distinct courts (from the ties + optional prop) sorted
  const gridCourts = useMemo(() => {
    const set = new Set(courts.filter(Boolean));
    ties.forEach((t) => t.court && set.add(t.court));
    matches.forEach((m) => m.court && set.add(m.court));
    return [...set].sort();
  }, [ties, matches, courts]);

  // Distinct dates present
  const dates = useMemo(() => {
    const s = new Set();
    ties.forEach((t) => {
      const d = toDate(t.scheduled_at);
      if (d && !isNaN(d)) s.add(fmtDate(d));
    });
    return [...s].sort();
  }, [ties]);

  // Slot rows per date (start .. end at 30 min, padded ±4 rows so admins can
  // drop into slots outside the current tie range).
  const SLOT_PAD = 4;
  const slotsByDate = useMemo(() => {
    const m = {};
    dates.forEach((day) => {
      const dayTies = ties.filter((t) => {
        const d = toDate(t.scheduled_at);
        return d && fmtDate(d) === day;
      });
      if (dayTies.length === 0) return;
      const starts = dayTies.map((t) => slotStart(toDate(t.scheduled_at))).sort((a, b) => a - b);
      const first = new Date(starts[0]);
      const last = new Date(starts[starts.length - 1]);
      first.setUTCMinutes(first.getUTCMinutes() - SLOT_PAD * SLOT_MINUTES);
      last.setUTCMinutes(last.getUTCMinutes() + SLOT_PAD * SLOT_MINUTES);
      const rows = [];
      const cur = new Date(first);
      while (cur <= last) {
        rows.push(new Date(cur));
        cur.setUTCMinutes(cur.getUTCMinutes() + SLOT_MINUTES);
      }
      m[day] = rows;
    });
    return m;
  }, [dates, ties]);

  const tieAt = (day, slot, court) => {
    return ties.find((t) => {
      if (t.court !== court) return false;
      const d = toDate(t.scheduled_at);
      if (!d) return false;
      if (fmtDate(d) !== day) return false;
      const s = slotStart(d);
      return s.getTime() === slot.getTime();
    });
  };

  const applyTieMove = async (tie, targetSlot, targetCourt) => {
    // Determine the delta: new tie start = target slot at target court on target day
    const newIso = new Date(targetSlot).toISOString();
    setBusyCells((prev) => ({ ...prev, [tie.tie_id]: true }));
    try {
      // Move seed 1 → target court; seeds 2/3 keep their courts (relative offset).
      const sortedSeeds = [...tie.seeds].sort((a, b) => (a.seed || 0) - (b.seed || 0));
      // For each seed, adjust scheduled_at to newIso (all seeds share the same start).
      // Only seed 1's court moves to targetCourt; seeds 2/3 stay on their existing courts.
      const results = await Promise.all(sortedSeeds.map((s, idx) => {
        const body = { scheduled_at: newIso };
        if (idx === 0) body.court = targetCourt;
        return api.patch(`/matches/${s.id}/schedule`, body).catch((e) => ({ error: e }));
      }));
      const errs = results.filter((r) => r && r.error);
      if (errs.length) {
        const msg = errs[0].error?.response?.data?.detail || "Reschedule blocked (court conflict?)";
        toast.error(msg);
      } else {
        toast.success(`Moved ${tie.team_a?.name || ""} vs ${tie.team_b?.name || ""} → ${fmtSlotLabel(targetSlot)} · ${targetCourt}`);
        onReload && onReload();
      }
    } finally {
      setBusyCells((prev) => ({ ...prev, [tie.tie_id]: false }));
    }
  };

  const handleDrop = (day, slot, court) => (e) => {
    e.preventDefault();
    if (!dragTie) return;
    const existing = tieAt(day, slot, court);
    if (existing && existing.tie_id === dragTie.tie_id) return;
    if (existing) {
      toast.error("A tie already occupies that slot — move it first.");
      return;
    }
    applyTieMove(dragTie, slot, court);
    setDragTie(null);
  };

  if (ties.length === 0) {
    return (
      <Card className="p-8 bg-[#0f1a15] border-white/10 text-center text-zinc-500 text-sm">
        No group-stage ties yet. Head to <b className="text-white">/admin/groups → Create Draws</b> to generate them.
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="draw-grid">
      {dates.map((day) => (
        <Card key={day} className="p-4 bg-[#0f1a15] border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="font-anton text-lg uppercase tracking-tight text-white">
              <span className="text-[#F4B400]">{day}</span>
              <span className="ml-3 text-xs text-zinc-500 normal-case font-sans font-normal">Drag any tie card to a different court / time slot</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">30-min slots · UTC</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-1" data-testid={`draw-grid-day-${day}`}>
              <thead>
                <tr>
                  <th className="w-16 text-[10px] uppercase text-zinc-500 text-left px-2">Time</th>
                  {gridCourts.map((c) => (
                    <th key={c} className="text-[10px] uppercase text-zinc-400 text-left px-2 py-1 bg-black/20 rounded" data-testid={`grid-col-${c}`}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(slotsByDate[day] || []).map((slot) => (
                  <tr key={slot.toISOString()}>
                    <td className="text-[11px] text-zinc-500 pr-2 align-top font-mono">{fmtSlotLabel(slot)}</td>
                    {gridCourts.map((c) => {
                      const tie = tieAt(day, slot, c);
                      const cellTestId = `cell-${day}-${fmtSlotLabel(slot).replace(":", "")}-${c.replace(/\s/g, "")}`;
                      return (
                        <td
                          key={c}
                          data-testid={cellTestId}
                          onDragOver={(e) => { if (dragTie) e.preventDefault(); }}
                          onDrop={handleDrop(day, slot, c)}
                          className={`h-16 min-w-[160px] rounded ${dragTie ? "bg-black/30 border border-dashed border-[#00A86B]/40" : tie ? "bg-transparent" : "bg-black/10"} align-top p-1`}
                        >
                          {tie && (
                            <div
                              draggable
                              onDragStart={() => setDragTie(tie)}
                              onDragEnd={() => setDragTie(null)}
                              className={`h-full rounded-lg border border-white/10 bg-gradient-to-b from-[#11241b] to-[#0a1611] p-2 cursor-grab active:cursor-grabbing hover:border-[#F4B400]/60 transition-colors ${busyCells[tie.tie_id] ? "opacity-60" : ""}`}
                              data-testid={`tie-card-${tie.tie_id}`}
                            >
                              <div className="flex items-start gap-1">
                                <GripVertical className="w-3 h-3 text-zinc-500 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] uppercase text-[#F4B400] tracking-widest truncate">Group {tie.group || "?"}</div>
                                  <div className="text-xs text-white font-medium truncate">{tie.team_a?.name || "TBD"}</div>
                                  <div className="text-[10px] text-zinc-500 -my-0.5">vs</div>
                                  <div className="text-xs text-white font-medium truncate">{tie.team_b?.name || "TBD"}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}
